/**
 * KCIS™ Adaptive Ecosystem Conversion Engine — canonical frontend route.
 *
 * Stateless renderer:
 *   - Frontend has ZERO scoring logic.
 *   - Backend dictates `stage`, `questions`, `progress_percent`, and final outcome.
 *
 * Per Sprint B locked direction:
 *   - Unified KCIS™ purple aesthetic across all doorways (subtle accent only post-routing).
 *   - No live confidence/score exposure during the flow.
 *   - Calm, reflective stage transitions (Pause interstitial after submit).
 *   - Mobile-first; sticky bottom continue on small screens.
 *   - Session persistence survives refresh / tab close / mobile suspension.
 *
 * Flag-off behavior: when KCIS_ADAPTIVE_ENABLED=false on the server, /start
 * returns 503 — frontend renders a graceful fallback pointing users to the
 * existing /wellness-assessment route.
 */
import { useEffect, useReducer, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import "@/pages/Assessment.css";

import AssessmentShell from "@/components/assessment/AssessmentShell";
import WelcomeScreen from "@/components/assessment/WelcomeScreen";
import QuestionCard from "@/components/assessment/QuestionCard";
import ProgressIndicator from "@/components/assessment/ProgressIndicator";
import StageTransition from "@/components/assessment/StageTransition";
import OutcomeScreen from "@/components/assessment/OutcomeScreen";
import ResumeBanner from "@/components/assessment/ResumeBanner";

import {
  assessmentApi,
  detectExplicitSource,
  isFeatureFlagOff,
  isSessionGone,
  sessionStore,
} from "@/lib/assessmentSession";

const INITIAL = {
  phase: "loading",                  // 'loading' | 'welcome' | 'stage' | 'transition' | 'outcome' | 'flag_off' | 'error'
  sessionId: null,
  stage: "core",
  sourceDomain: "direct",
  questions: [],
  progressPercent: 0,
  doorConfidences: null,
  outcome: null,
  answers: {},                       // stage-scoped buffer; cleared on stage advance
  identity: { first_name: "", email: "" },
  error: null,
  resuming: false,
  beginning: false,
  submitting: false,
  priorStageForTransition: null,     // used by StageTransition to render the right reflection
};

function reducer(state, action) {
  switch (action.type) {
    case "FLAG_OFF":
      sessionStore.clear();
      return { ...INITIAL, phase: "flag_off" };
    case "WELCOME":
      return { ...INITIAL, phase: "welcome", sourceDomain: action.sourceDomain || "direct" };
    case "BEGINNING":
      return { ...state, beginning: true, error: null };
    case "STAGE_LOADED":
      return {
        ...state,
        phase: "stage",
        beginning: false,
        submitting: false,
        sessionId: action.payload.session_id,
        stage: action.payload.stage,
        sourceDomain: action.payload.source_domain || state.sourceDomain || "direct",
        questions: action.payload.questions || [],
        progressPercent: action.payload.progress_percent || 0,
        doorConfidences: action.payload.door_confidences || null,
        answers: {},                  // fresh stage = fresh buffer
        error: null,
      };
    case "RESUMED":
      // If session is already complete, the backend returns an OutcomeResponse
      // (has `recommended_cta`); rehydrate directly into the outcome view.
      if (action.payload.stage === "complete" || action.payload.recommended_cta) {
        return {
          ...INITIAL,
          phase: "outcome",
          sessionId: action.payload.session_id,
          stage: "complete",
          sourceDomain: action.payload.source_domain || state.sourceDomain || "direct",
          progressPercent: 100,
          outcome: action.payload,
        };
      }
      return {
        ...state,
        phase: "stage",
        beginning: false,
        resuming: true,
        sessionId: action.payload.session_id,
        stage: action.payload.stage,
        sourceDomain: action.payload.source_domain || "direct",
        questions: action.payload.questions || [],
        progressPercent: action.payload.progress_percent || 0,
        doorConfidences: action.payload.door_confidences || null,
        answers: {},
        error: null,
      };
    case "ANSWER":
      return { ...state, answers: { ...state.answers, [action.qid]: action.value } };
    case "IDENTITY":
      return { ...state, identity: { ...state.identity, ...action.payload } };
    case "SUBMITTING":
      return { ...state, submitting: true, error: null };
    case "ADVANCE_TO_TRANSITION":
      return {
        ...state,
        phase: "transition",
        priorStageForTransition: action.priorStage,
        submitting: false,
      };
    case "OUTCOME":
      return {
        ...state,
        phase: "outcome",
        submitting: false,
        outcome: action.payload,
        stage: "complete",
        progressPercent: 100,
      };
    case "ERROR":
      return { ...state, error: action.error, submitting: false, beginning: false };
    case "DISMISS_RESUME":
      return { ...state, resuming: false };
    case "RESET":
      sessionStore.clear();
      return { ...INITIAL, phase: "welcome", sourceDomain: action.sourceDomain || "direct" };
    default:
      return state;
  }
}

function canContinue(state) {
  if (!state.questions.length) return false;
  for (const q of state.questions) {
    if (q.optional) continue;
    const v = state.answers[q.id];
    if (q.type === "multi-select") {
      if (!Array.isArray(v) || v.length === 0) return false;
    } else if (q.type === "single-select") {
      if (!v) return false;
    } else if (q.type === "slider") {
      if (typeof v !== "number") return false;
    } else if (q.type === "open-text") {
      // open-text optional flag wins; required open-text needs ≥1 char
      if (!v || !String(v).trim()) return false;
    }
  }
  if (state.stage === "profile") {
    // Identity collected at profile stage (email required, first_name optional but encouraged).
    if (!state.identity.email || !/\S+@\S+\.\S+/.test(state.identity.email)) return false;
  }
  return true;
}

export default function Assessment() {
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const bootedRef = useRef(false);
  const [headerOffset, setHeaderOffset] = useState(0);
  const cardScrollRef = useRef(null);

  // ───────── Boot: try to resume; else show welcome ─────────
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    const explicitSource = detectExplicitSource();
    const existingId = sessionStore.read();

    const boot = async () => {
      try {
        // health: detects feature-flag state without consuming a session.
        const h = await assessmentApi.health();
        if (!h.feature_flag_enabled) {
          dispatch({ type: "FLAG_OFF" });
          return;
        }
      } catch {
        // health endpoint should always work; if not, fall through and try resume.
      }

      if (existingId) {
        try {
          const data = await assessmentApi.get(existingId);
          dispatch({ type: "RESUMED", payload: data });
          return;
        } catch (err) {
          if (isFeatureFlagOff(err)) {
            dispatch({ type: "FLAG_OFF" });
            return;
          }
          if (isSessionGone(err)) {
            sessionStore.clear();
          }
        }
      }
      dispatch({ type: "WELCOME", sourceDomain: explicitSource || "direct" });
    };

    boot();
  }, []);

  // Auto-dismiss resume banner after first interaction
  useEffect(() => {
    if (!state.resuming) return;
    const t = setTimeout(() => dispatch({ type: "DISMISS_RESUME" }), 4500);
    return () => clearTimeout(t);
  }, [state.resuming]);

  // Scroll to top of card on stage / transition change
  useEffect(() => {
    if (cardScrollRef.current) {
      try {
        window.scrollTo({ top: cardScrollRef.current.offsetTop - 24, behavior: "smooth" });
      } catch {
        window.scrollTo(0, 0);
      }
    }
  }, [state.stage, state.phase]);

  // ───────── Handlers ─────────
  const handleBegin = async () => {
    dispatch({ type: "BEGINNING" });
    try {
      const data = await assessmentApi.start({ source: detectExplicitSource() });
      dispatch({ type: "STAGE_LOADED", payload: data });
    } catch (err) {
      if (isFeatureFlagOff(err)) return dispatch({ type: "FLAG_OFF" });
      dispatch({ type: "ERROR", error: "We couldn't start your reflection. Please try again." });
    }
  };

  const handleAnswerChange = (qid, value) =>
    dispatch({ type: "ANSWER", qid, value });

  const handleContinue = async () => {
    if (!state.sessionId || state.submitting || !canContinue(state)) return;
    dispatch({ type: "SUBMITTING" });
    const priorStage = state.stage;

    try {
      let response;
      if (state.stage === "profile") {
        response = await assessmentApi.submitStage(state.sessionId, "profile", state.answers, {
          first_name: state.identity.first_name || null,
          email: state.identity.email || null,
        });
        // profile submit returns OutcomeResponse
        dispatch({ type: "OUTCOME", payload: response });
        return;
      }
      response = await assessmentApi.submitStage(state.sessionId, state.stage, state.answers);
      // Show calm interstitial; user clicks "Continue when ready" to enter next stage.
      dispatch({ type: "ADVANCE_TO_TRANSITION", priorStage });
      // Stash the next stage payload so transition → stage hand-off is instant.
      window.__kcisPendingStagePayload = response;
    } catch (err) {
      if (isFeatureFlagOff(err)) return dispatch({ type: "FLAG_OFF" });
      if (isSessionGone(err)) {
        sessionStore.clear();
        return dispatch({ type: "RESET", sourceDomain: state.sourceDomain });
      }
      dispatch({ type: "ERROR", error: "We couldn't save your responses. Please try again." });
    }
  };

  const handleTransitionContinue = () => {
    const next = window.__kcisPendingStagePayload;
    window.__kcisPendingStagePayload = null;
    if (!next) {
      // fallback: refetch session
      if (state.sessionId) {
        assessmentApi
          .get(state.sessionId)
          .then((data) => dispatch({ type: "STAGE_LOADED", payload: data }))
          .catch(() => dispatch({ type: "ERROR", error: "Couldn't load the next step." }));
      }
      return;
    }
    dispatch({ type: "STAGE_LOADED", payload: next });
  };

  const handleStartOver = () => {
    sessionStore.clear();
    window.__kcisPendingStagePayload = null;
    dispatch({ type: "RESET", sourceDomain: detectExplicitSource() || "direct" });
  };

  // ───────── Renders ─────────
  if (state.phase === "loading") {
    return (
      <AssessmentShell>
        <div className="flex items-center justify-center min-h-[40vh]" data-testid="kcis-loading">
          <Loader2 className="h-6 w-6 animate-spin text-purple-900" />
        </div>
      </AssessmentShell>
    );
  }

  if (state.phase === "flag_off") {
    return (
      <AssessmentShell>
        <section
          className="kcis-assessment-card p-8 sm:p-10 text-center kcis-fade-in"
          data-testid="kcis-flag-off"
        >
          <p className="kcis-assessment-overline mb-4">A note from KCIS™</p>
          <h2 className="kcis-assessment-display text-2xl sm:text-3xl mb-3">
            The new adaptive reflection isn't open yet.
          </h2>
          <p className="text-slate-600 max-w-lg mx-auto mb-6">
            We're putting the finishing touches on it. In the meantime, the
            existing Wellness Gateway is ready to meet you.
          </p>
          <Link
            to="/wellness-assessment"
            data-testid="kcis-flag-off-fallback-link"
            className="inline-flex items-center px-6 py-3 rounded-full bg-purple-900 hover:bg-purple-800 text-white text-sm tracking-wide transition"
          >
            Continue with the Wellness Gateway →
          </Link>
        </section>
      </AssessmentShell>
    );
  }

  if (state.phase === "welcome") {
    return (
      <AssessmentShell>
        <WelcomeScreen
          sourceDomain={state.sourceDomain}
          onBegin={handleBegin}
          beginning={state.beginning}
        />
        {state.error && (
          <p className="mt-4 text-center text-sm text-rose-700" data-testid="kcis-welcome-error">
            {state.error}
          </p>
        )}
      </AssessmentShell>
    );
  }

  if (state.phase === "transition") {
    return (
      <AssessmentShell>
        <StageTransition
          priorStage={state.priorStageForTransition || state.stage}
          onContinue={handleTransitionContinue}
        />
      </AssessmentShell>
    );
  }

  if (state.phase === "outcome") {
    return (
      <AssessmentShell hideProgress>
        <OutcomeScreen
          outcome={state.outcome}
          firstName={state.identity.first_name}
          onStartOver={handleStartOver}
        />
      </AssessmentShell>
    );
  }

  // Stage phase
  return (
    <AssessmentShell
      progress={
        <ProgressIndicator
          stage={state.stage}
          progressPercent={state.progressPercent}
        />
      }
    >
      <div ref={cardScrollRef} className="space-y-5">
        {state.resuming && <ResumeBanner onStartOver={handleStartOver} />}

        {state.questions.map((q, idx) => (
          <QuestionCard
            key={q.id}
            question={q}
            value={state.answers[q.id]}
            onChange={(v) => handleAnswerChange(q.id, v)}
            index={idx}
          />
        ))}

        {state.stage === "profile" && (
          <section
            className="kcis-assessment-card p-6 sm:p-8 kcis-fade-in"
            data-testid="kcis-identity-card"
          >
            <p className="kcis-assessment-overline mb-3">Before we close</p>
            <h3 className="kcis-assessment-display text-xl sm:text-2xl mb-4">
              How would you like us to address you?
            </h3>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="First name (optional)"
                value={state.identity.first_name}
                onChange={(e) =>
                  dispatch({ type: "IDENTITY", payload: { first_name: e.target.value } })
                }
                data-testid="kcis-identity-first-name"
                className="w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-300 transition"
              />
              <input
                type="email"
                placeholder="Email address"
                value={state.identity.email}
                onChange={(e) =>
                  dispatch({ type: "IDENTITY", payload: { email: e.target.value } })
                }
                data-testid="kcis-identity-email"
                required
                className="w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-300 transition"
              />
              <p className="text-xs text-slate-500">
                We'll only use this to share your reflection. No spam, ever.
              </p>
            </div>
          </section>
        )}

        {state.error && (
          <p className="text-sm text-rose-700 text-center" data-testid="kcis-stage-error">
            {state.error}
          </p>
        )}

        <div className="kcis-sticky-cta">
          <button
            type="button"
            onClick={handleContinue}
            disabled={!canContinue(state) || state.submitting}
            data-testid="kcis-stage-continue"
            className="w-full sm:w-auto sm:mx-auto sm:block inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-purple-900 hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed text-white text-base tracking-wide shadow-md transition"
          >
            {state.submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : state.stage === "profile" ? (
              "See my reflection →"
            ) : (
              "Continue →"
            )}
          </button>
        </div>
      </div>
    </AssessmentShell>
  );
}
